import type { GameState } from "@fiveout/core";
import {
  aValue,
  buildStacks,
  draws,
  showing,
  TUTORIAL_OPPONENT_ID,
  TUTORIAL_OPPONENT_NAME,
  TUTORIAL_PLAYER_ID,
  TUTORIAL_PLAYER_NAME,
  tutorialPlayer,
  tutorialRound,
  type Tutorial,
  type TutorialStep,
} from "./script";
import { BASICS_DECK_CONFIG } from "./basics";

/**
 * Tutorial – Bonus Moves: every way to play more than one card in a turn, and
 * the end of a round.
 *
 * A two-player round against Sam, played to the finish, because the last two
 * lessons need one: Vinny's Rule only bites on a player's LAST card, and the
 * last lap is the other player's extra turn. So the player's deck is exactly
 * the seven cards the script plays — every one primed — and running it out is
 * the point rather than a hazard. Sam plays one card between each of the
 * player's turns, always a right guess on a pile the next lesson leaves alone.
 *
 * Vinny's Rule is the only house rule on. Indices are 0 center, 1 up, 2 down,
 * 3 left, 4 right; piles are top-first.
 */
const BOARD: number[][] = [
  [3, 10], // 0 center — the second bonus card
  [7, 2, 11], // 1 up — the off-by-one Match
  [6], // 2 down — the duplicate
  [12, 4, 9, 1, 8, 5], // 3 left — the first bonus card, then the tallest pile for the last card
  [], // 4 right — empty, for the free card
];

/** Sam's cards after the four the script plays. Never drawn. */
const SAM_FILLER = [8, 3, 13, 6, 1, 9, 5, 12];

const SAM = TUTORIAL_OPPONENT_ID;

/** Every card the player holds, in the order the steps play them. */
const YOURS = {
  closeMatch: draws("tbm-close", [8]),
  bonus: draws("tbm-bonus", [5, 11]),
  duplicate: draws("tbm-dup", [6, 10]),
  free: draws("tbm-free", [4]),
  last: draws("tbm-last", [9]),
};

const SAMS = {
  first: draws("tbm-sam1", [2]),
  second: draws("tbm-sam2", [4]),
  third: draws("tbm-sam3", [12]),
  fourth: draws("tbm-sam4", [10]),
};

function initialState(): GameState {
  return tutorialRound({
    id: "tutorial-bonus-moves",
    deckConfig: BASICS_DECK_CONFIG,
    houseRules: { vinnysRule: true },
    stacks: buildStacks("tbm", BOARD),
    players: [
      tutorialPlayer(TUTORIAL_PLAYER_ID, TUTORIAL_PLAYER_NAME, [
        ...YOURS.closeMatch,
        ...YOURS.bonus,
        ...YOURS.duplicate,
        ...YOURS.free,
        ...YOURS.last,
      ]),
      tutorialPlayer(SAM, TUTORIAL_OPPONENT_NAME, [
        ...SAMS.first,
        ...SAMS.second,
        ...SAMS.third,
        ...SAMS.fourth,
        ...draws("tbm-sam-deck", SAM_FILLER),
      ]),
    ],
  });
}

const STEPS: TutorialStep[] = [
  {
    id: "welcome",
    title: "Bonus moves",
    prompt:
      "This time you're playing against **Sam**. You take turns: you, then Sam, then you again.\n\n" +
      "Bonus moves let you play more than one card in a turn, which is how you pull ahead. Each move here is set up to show you one.",
    cta: "Let's go",
    pointAt: { deck: true, opponentDeck: true },
  },
  {
    id: "close-match",
    title: "Off by 1",
    prompt: (s) => `The **top** pile shows ${aValue(showing(s, 1))}. Tap = to guess Match.`,
    plays: [
      {
        move: { kind: "guess", stackIndex: 1, guess: "match" },
        draws: YOURS.closeMatch,
        pauses: [
          {
            on: "card-played",
            at: "reveal",
            text: "**An 8.** Not the same number, but only 1 away from the 7. A Match off by 1 is close enough: your card stays.",
          },
        ],
      },
    ],
  },
  {
    id: "bonus-cards",
    title: "Place your bonus cards",
    prompt:
      "Off by 1 also earns **two bonus cards**, and you choose where they go: any other piles. Bonus cards always stay.\n\n" +
      "Tap **+** on the **left** pile, then on the **middle** pile.",
    plays: [
      { move: { kind: "bonus", stackIndex: 3 }, draws: YOURS.bonus.slice(0, 1) },
      { move: { kind: "bonus", stackIndex: 0 }, draws: YOURS.bonus.slice(1) },
    ],
    debrief: "Three cards gone in one turn. Now it's Sam's turn.",
    cta: "Next",
  },
  {
    id: "duplicate",
    title: "The same number",
    watching: "You take turns: Sam plays a card, then it's your turn again.",
    prompt: (s) => `Your turn. The **bottom** pile shows ${aValue(showing(s, 2))}. Tap ▲ to guess Higher.`,
    plays: [
      { by: SAM, move: { kind: "guess", stackIndex: 1, guess: "lower" }, draws: SAMS.first },
      {
        move: { kind: "guess", stackIndex: 2, guess: "higher" },
        draws: YOURS.duplicate,
        pauses: [
          {
            on: "card-played",
            at: "reveal",
            text: "**A 6**, the **same number** as the pile. That's neither right nor wrong: it stays, and your next card goes on the same pile with the same guess.",
          },
          { on: "card-played", nth: 2, at: "reveal", text: "**A 10.** Higher than 6, so it stays too, and your turn ends." },
        ],
      },
    ],
  },
  {
    id: "empty-pile",
    title: "An empty pile",
    prompt:
      "The **right** pile is **empty**. Any card can go on an empty pile for free: no guess, and it always stays. So can a card on a wild.\n\n" +
      "Tap **+** on the right pile.",
    plays: [
      { by: SAM, move: { kind: "guess", stackIndex: 0, guess: "lower" }, draws: SAMS.second },
      { move: { kind: "free", stackIndex: 4 }, draws: YOURS.free },
    ],
    debrief: "Free, and it ended your turn. A free spot can't go wrong, so it's often the safest move you have.",
    cta: "Next",
  },
  {
    id: "last-card",
    title: "Vinny's Rule",
    prompt:
      "You're down to your **last card**. **Vinny's Rule:** your last card has to go on the **tallest** pile, the one holding the most cards. The rest are blocked.\n\n" +
      "Tap ▲ on the left pile.",
    plays: [
      { by: SAM, move: { kind: "guess", stackIndex: 2, guess: "higher" }, draws: SAMS.third },
      {
        move: { kind: "guess", stackIndex: 3, guess: "higher" },
        draws: YOURS.last,
        pauses: [{ on: "card-played", at: "reveal", text: "**A 9.** Higher than 5, and that was your last card." }],
      },
    ],
    debrief:
      "**You're out of cards!** The round isn't over yet, though. Everyone gets the same number of turns, so Sam gets one more.",
    cta: "Next",
    pointAt: { deck: true, stacks: [3] },
  },
  {
    id: "last-lap",
    title: "The last turn",
    prompt: "You went first, so Sam has had one turn fewer. Sam gets one last turn to catch up.",
    plays: [{ by: SAM, move: { kind: "guess", stackIndex: 4, guess: "higher" }, draws: SAMS.fourth }],
    debrief:
      "Now the round is over. Players are placed by **fewest cards left**: you're 1st and score 1 point, Sam is 2nd and scores 2.\n\n" +
      "Play more rounds and the points add up. **Lowest total** wins.",
    cta: "Next",
  },
  {
    id: "done",
    title: "That's every bonus move",
    prompt:
      "**Five Out** (an exact Match) is the biggest: a bonus card on every other pile. Off by 1, the same number and a free spot all help too.\n\n" +
      "Next, **Wild Cards** shows the special cards.",
    cta: "Play Five Out",
  },
];

export const BONUS_MOVES: Tutorial = {
  id: "bonus-moves",
  title: "Tutorial – Bonus Moves",
  initialState,
  steps: STEPS,
};
